import React, { useState, useEffect, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { usePrefetch } from '../hooks/usePrefetch';
import {
  LayoutGrid,
  Building2,
  Calendar,
  DollarSign,
  Users,
  Tag,
  Settings,
  Menu,
  X,
  User,
  LogOut,
  CreditCard,
  Grid3x3,
  Receipt,
  Star,
  AlertCircle,
  Wallet,
  Bell,
  QrCode,
  UserCog,
  Activity
} from 'lucide-react';
import QrScannerModal from '../components/modals/QrScannerModal';
import { useVenueRole } from '../hooks/useVenueRole';

interface DashboardLayoutProps {
  children: React.ReactNode;
}

// Map routes to prefetch tab names
const routeToPrefetchMap: Record<string, Parameters<ReturnType<typeof usePrefetch>['prefetchTab']>[0]> = {
  '/overview': 'overview',
  '/bookings': 'bookings',
  '/revenue': 'revenue',
  '/payouts': 'revenue', // Shares cache with revenue (same query key)
  '/transactions': 'transactions',
  '/courts': 'courts',
  '/venue-info': 'venue-info',
  '/promotions': 'promotions',
  '/customer-insights': 'customer-insights',
  '/memberships': 'memberships',
  '/reviews': 'reviews',
};

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isScannerOpen, setIsScannerOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, refreshUser } = useAuth();

  // Refresh user/venue data once on mount so that business_model changes
  // made in super admin are reflected immediately without requiring re-login.
  useEffect(() => {
    refreshUser().catch(() => {/* silently ignore — user will still see cached data */});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle logout with navigation to root
  const handleLogout = useCallback(async () => {
    await logout();
    navigate('/', { replace: true });
  }, [logout, navigate]);

  // Get venue info for prefetching
  const venue = user?.venues?.[0];
  const isSplitVenue = venue?.payment_mode === 'split_payment';

  // Derive capability flags from business_model.
  // Default (null/unknown) → show everything, same as hybrid.
  const businessModel = venue?.business_model ?? 'gym_sports_hybrid';
  const hasSportsBooking = businessModel !== 'gym_only';
  const hasGymMembership = businessModel === 'gym_only' || businessModel === 'gym_sports_hybrid';

  const { prefetchCommonTabs, createHoverHandler } = usePrefetch(venue?.id, venue?.slug, user?.id);

  // Role-aware gating (STAFF_ACCESS_PLAN §7): staff see only the tabs their
  // capability set covers; money tabs, Overview and Team are never shown to
  // staff. The backend enforces all of this — hiding tabs is UX, not security.
  const { isStaff, can } = useVenueRole();

  // Prefetch common tabs when dashboard loads — but only the ones this user
  // can actually reach (staff-access): prefetching owner-only endpoints for
  // staff fires background 403s and trips rate limits.
  useEffect(() => {
    if (venue?.id) {
      // Delay prefetching to not block initial render
      const timer = setTimeout(() => {
        prefetchCommonTabs((tab) => {
          switch (tab) {
            case 'overview':
            case 'revenue':
            case 'transactions':
              return !isStaff; // owner-only (Overview is money-flavored)
            case 'bookings':
              return can('schedule.view');
            case 'courts':
              return can('venue.edit') || can('schedule.view');
            case 'customer-insights':
              return can('insights.view');
            default:
              return true;
          }
        });
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [venue?.id, prefetchCommonTabs, isStaff, can]);

  const navigationItems = [
    // Overview is owner-only: its endpoint is owner-only and it surfaces
    // revenue/payout figures. Staff land on their first operational tab.
    ...(!isStaff ? [{ path: '/overview', icon: LayoutGrid, label: 'Overview' }] : []),
    ...(can('venue.edit') ? [{ path: '/venue-info', icon: Building2, label: 'Venue Info' }] : []),
    // Sports-only tabs — hidden for gym-only venues
    ...(hasSportsBooking ? [
      ...(can('venue.edit') || can('schedule.view') ? [{ path: '/courts', icon: Grid3x3, label: 'Courts/Fields' }] : []),
      ...(can('schedule.view') ? [{ path: '/bookings', icon: Calendar, label: 'Bookings' }] : []),
    ] : []),
    // Money tabs — owner only
    ...(!isStaff ? [
      { path: '/revenue', icon: DollarSign, label: 'Revenue' },
      // Payouts tab is managed by Paystack for split-payment venues — hide it for those
      ...(!isSplitVenue ? [{ path: '/payouts', icon: Wallet, label: 'Payouts' }] : []),
      { path: '/debts', icon: AlertCircle, label: 'Debts & Refunds' },
      { path: '/transactions', icon: Receipt, label: 'Transactions' },
    ] : []),
    // Gym tab — only shown for gym_only and gym_sports_hybrid venues
    ...(hasGymMembership && can('memberships.manage') ? [{ path: '/memberships', icon: CreditCard, label: 'Memberships' }] : []),
    ...(can('insights.view') ? [{ path: '/customer-insights', icon: Users, label: 'Customer Insights' }] : []),
    ...(can('pricing.manage') ? [{ path: '/promotions', icon: Tag, label: 'Promotions' }] : []),
    ...(can('insights.view') || can('reviews.reply') ? [{ path: '/reviews', icon: Star, label: 'Reviews' }] : []),
    // Team — owner only (staff.manage is never grantable)
    ...(!isStaff ? [{ path: '/team', icon: UserCog, label: 'Team' }] : []),
    // Activity feed — "who did what" (Phase 4)
    ...(can('activity.view') ? [{ path: '/activity', icon: Activity, label: 'Activity' }] : []),
    { path: '/settings',          icon: Settings,    label: 'Settings' },
  ];

  // Staff landing / forbidden-route guard (staff-access): a staff member's
  // nav omits Overview, money tabs and Team. If they reach one anyway — the
  // default '/' → /overview landing, a typed URL, or a stale SPA route —
  // send them to their first available tab instead of a page that 403s.
  // Owners keep full access (effect is a no-op for them).
  const staffAllowedPaths = navigationItems.map(item => item.path).join(',');
  useEffect(() => {
    if (!isStaff) return;
    const allowed = new Set([...staffAllowedPaths.split(','), '/support']);
    if (!allowed.has(location.pathname)) {
      const firstPath = navigationItems[0]?.path ?? '/settings';
      if (location.pathname !== firstPath) {
        navigate(firstPath, { replace: true });
      }
    }
    // navigationItems is derived from staffAllowedPaths; depend on the stable string
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isStaff, location.pathname, staffAllowedPaths, navigate]);

  const getPageTitle = () => {
    const currentItem = navigationItems.find(item => item.path === location.pathname);
    return currentItem?.label || 'Dashboard';
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* Sidebar */}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-gray-200">
            <div className="flex flex-col">
              <img
                src="/spotts-logo-dark.svg"
                alt="Spotts Logo"
                className="h-10 w-auto"
              />
              <p className="text-xs font-normal text-gray-600 mt-2 tracking-wide">
                Find your <span className="text-blue-600 italic font-medium">spot</span>. Play your <span className="text-blue-600 italic font-medium">sport</span>.
              </p>
            </div>
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="lg:hidden p-1 rounded-md hover:bg-gray-100"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-4 space-y-1 overflow-y-auto">
            {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const prefetchTabName = routeToPrefetchMap[item.path];
              const hoverHandlers = prefetchTabName ? createHoverHandler(prefetchTabName) : {};

              return (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={`flex items-center space-x-3 px-4 py-2.5 rounded-xl transition-all ${
                    isActive
                      ? 'bg-blue-600 text-white shadow-lg'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                  onClick={() => setIsSidebarOpen(false)}
                  {...hoverHandlers}
                >
                  <Icon className={`h-5 w-5 ${isActive ? 'text-white' : 'text-gray-500'}`} />
                  <span className={`text-sm ${isActive ? 'text-white font-semibold' : 'text-gray-700'}`}>
                    {item.label}
                  </span>
                </NavLink>
              );
            })}
          </nav>

          {/* Scan QR Code — quick action button above profile (check-in cap) */}
          {can('checkin') && (
            <div className="px-4 pb-3">
              <button
                onClick={() => { setIsSidebarOpen(false); setIsScannerOpen(true); }}
                className="w-full flex items-center space-x-3 px-4 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 transition-colors shadow-md"
              >
                <QrCode className="h-5 w-5 text-white flex-shrink-0" />
                <span className="text-sm font-semibold text-white">Scan QR Code</span>
              </button>
            </div>
          )}

          {/* User Profile at Bottom */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center space-x-3 px-4 py-3 rounded-lg border border-gray-200 bg-gray-50">
              <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                <User className="h-4 w-4 text-blue-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-gray-900 truncate">{user?.name || 'Vendor Admin'}</p>
                <p className="text-xs text-gray-500 truncate">{user?.email || 'vendor@example.com'}</p>
              </div>
              <button
                onClick={handleLogout}
                className="p-1.5 rounded-md hover:bg-gray-200 flex-shrink-0"
                title="Logout"
              >
                <LogOut className="h-4 w-4 text-gray-500 hover:text-gray-700" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Mobile Header with Hamburger */}
        <div className="lg:hidden flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
          <button
            onClick={() => setIsSidebarOpen(true)}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Open navigation menu"
          >
            <Menu className="h-6 w-6 text-gray-700" />
          </button>
          <h1 className="text-lg font-black text-gray-900">{getPageTitle()}</h1>
          {can('checkin') ? (
            <button
              onClick={() => setIsScannerOpen(true)}
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
              aria-label="Scan QR code"
            >
              <QrCode className="h-6 w-6 text-blue-600" />
            </button>
          ) : (
            <span className="w-10" />
          )}
        </div>

        {/* Page Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>

      {/* Overlay for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setIsSidebarOpen(false)}
        />
      )}

      {/* QR Scanner Modal */}
      <QrScannerModal isOpen={isScannerOpen} onClose={() => setIsScannerOpen(false)} />
    </div>
  );
};

export default DashboardLayout;